import {
  createScenarioApprovals,
  readScenarioApprovals,
  readScenarioApprovalsById,
  updateScenarioApprovals,
  deleteScenarioApprovals,
} from "@/services/api/scenario-approvals";
import {
  CreateScenarioApprovalsRequest,
  ReadScenarioApprovalsRequest,
  UpdateScenarioApprovalsByIdRequest,
} from "@/types/scenario-approvals";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../queryKeys";
import { APPROVAL_DIRECTION } from "@/consts/Approval";
import { ScenarioApprovalsResponse } from "@/types/scenario";
import request from "@/libs/request";

// 시나리오 분석 결재 생성
export const useCreateScenarioApprovals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateScenarioApprovalsRequest) =>
      createScenarioApprovals(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SCENARIO_APPROVALS.LIST_ROOT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SCENARIO.LIST(variables.project_group_id),
      });
    },
  });
};

// 시나리오 분석 결재 목록 조회
export const useReadScenarioApprovals = ({
  direction = APPROVAL_DIRECTION.REQUESTED,
  pagination,
  decision_filters,
}: ReadScenarioApprovalsRequest) => {
  return useQuery({
    queryKey: QUERY_KEYS.SCENARIO_APPROVALS.LIST(direction),
    queryFn: () =>
      readScenarioApprovals({ direction, pagination, decision_filters }),
  });
};

// 시나리오 분석 결재 조회
export const useReadScenarioApprovalsById = (approval_id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.SCENARIO_APPROVALS.DETAIL(approval_id),
    queryFn: () => readScenarioApprovalsById({ approval_id }),
    enabled: !!approval_id,
  });
};

// 시나리오 분석 결재 승인/반려
export const useUpdateScenarioApprovals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      approval_id,
      data,
    }: {
      approval_id: string;
      data: UpdateScenarioApprovalsByIdRequest;
    }) => updateScenarioApprovals({ approval_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SCENARIO_APPROVALS.LIST_ROOT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SCENARIO_APPROVALS.DETAIL(variables.approval_id),
      });
    },
  });
};

// 시나리오 분석 결재 삭제(취소)
export const useDeleteScenarioApprovals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ approval_id }: { approval_id: string }) =>
      deleteScenarioApprovals({ approval_id }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SCENARIO_APPROVALS.LIST_ROOT,
      });
      // 시나리오 목록도 갱신 (프로젝트 그룹별로)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "scenario" && query.queryKey[1] === "list",
      });
    },
  });
};

// 시나리오 승인 내역 조회 (새로운 API)
export const useReadScenarioApprovalsHistory = (group_id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.SCENARIO_APPROVALS.HISTORY(group_id),
    queryFn: async () => {
      const { data } = await request.post<ScenarioApprovalsResponse>(
        `/project-groups/${group_id}/read-approvals`
      );
      return data;
    },
    enabled: !!group_id,
  });
};
